/**
 * Admin Customers Page
 *
 * List and manage all customer accounts
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logger, LogCategory } from '@/utils/logger';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useAdminStore, useAllCompanies, useAdminLoading } from '@/stores/adminStore';
import { useToast } from '@/components/ui/Toast';
import type { Company } from '@/types';
import {
  Search,
  Users,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Plus,
} from 'lucide-react';

export function AdminCustomersPage() {
  const { fetchAllCompanies, createCompany } = useAdminStore();
  const companies = useAllCompanies();
  const isLoading = useAdminLoading();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    plan: 'starter' as 'trial' | 'starter' | 'growth' | 'pro' | 'enterprise',
    credits: 1000,
    isInternal: false,
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
    },
    settings: {
      notificationFrequency: 'weekly' as const,
      lowCreditThreshold: 100,
      autoRecharge: false,
      rechargeAmount: 1000,
      rechargeThreshold: 500,
      notificationsEnabled: true,
      emailNotifications: true,
      smsNotifications: false,
    },
  });

  useEffect(() => {
    Logger.info(LogCategory.UI, 'AdminCustomersPage mounted', 'AdminCustomersPage');
    fetchAllCompanies();
  }, [fetchAllCompanies]);

  const filteredCompanies = companies.filter((company) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      company.name.toLowerCase().includes(query) ||
      company.domain.toLowerCase().includes(query) ||
      company.id.toLowerCase().includes(query)
    );
  });

  const getPlanColor = (plan: string): 'default' | 'success' | 'warning' | 'danger' | 'info' | 'outline' => {
    switch (plan) {
      case 'trial':
        return 'warning';
      case 'enterprise':
        return 'info';
      case 'pro':
        return 'success';
      case 'growth':
        return 'info';
      case 'starter':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCreditStatus = (company: Company) => {
    const threshold = company.settings?.lowCreditThreshold || 100;
    if (company.credits < threshold) {
      return { label: 'Low', variant: 'warning' as const };
    }
    if (company.credits < threshold * 2) {
      return { label: 'Medium', variant: 'info' as const };
    }
    return { label: 'Healthy', variant: 'success' as const };
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.domain) {
      toast.error('Error', 'Company name and domain are required');
      return;
    }

    setIsCreating(true);
    try {
      const newCompany = await createCompany(formData);
      toast.success('Success', `Account created for ${newCompany.name}`);
      setShowCreateModal(false);
      // Reset form
      setFormData({
        name: '',
        domain: '',
        plan: 'starter',
        credits: 1000,
        isInternal: false,
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'USA',
        },
        settings: {
          notificationFrequency: 'weekly' as const,
          lowCreditThreshold: 100,
          autoRecharge: false,
          rechargeAmount: 1000,
          rechargeThreshold: 500,
          notificationsEnabled: true,
          emailNotifications: true,
          smsNotifications: false,
        },
      });
      // Navigate to the new company detail page
      navigate(`/admin/customers/${newCompany.id}`);
    } catch (error) {
      toast.error('Error', 'Failed to create account');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Customer Accounts</h2>
            <p className="text-slate-500 mt-1">Manage and view all customer accounts</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Account
          </Button>
        </div>

        {/* Search & Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, domain, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-slate-500">Total Customers</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{companies.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-slate-500">Enterprise</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {companies.filter((c) => c.plan === 'enterprise').length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-slate-500">Pro</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {companies.filter((c) => c.plan === 'pro').length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-slate-500">Starter</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {companies.filter((c) => c.plan === 'starter').length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Customers List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : filteredCompanies.length > 0 ? (
          <div className="space-y-3">
            {filteredCompanies.map((company) => {
              const creditStatus = getCreditStatus(company);
              const showLowCreditWarning = creditStatus.variant === 'warning';

              return (
                <Link
                  key={company.id}
                  to={`/admin/customers/${company.id}`}
                  className="block"
                >
                  <Card className="hover:border-purple-300 hover:shadow-md transition-all">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Company Icon */}
                          <div className="h-12 w-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                            <Users className="h-6 w-6 text-purple-600" />
                          </div>

                          {/* Company Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{company.name}</h3>
                              {showLowCreditWarning && (
                                <AlertTriangle className="h-4 w-4 text-warning-600" />
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-2">{company.domain}</p>

                            <div className="flex items-center gap-3 flex-wrap">
                              {company.isInternal && (
                                <Badge variant="info" size="sm">
                                  Internal
                                </Badge>
                              )}
                              <Badge variant={getPlanColor(company.plan)} size="sm">
                                {company.plan.charAt(0).toUpperCase() + company.plan.slice(1)}
                              </Badge>
                              {company.isInternal ? (
                                <Badge variant="info" size="sm">
                                  ∞ Unlimited
                                </Badge>
                              ) : (
                                <Badge variant={creditStatus.variant} size="sm">
                                  {company.credits.toLocaleString()} credits
                                </Badge>
                              )}
                              <span className="text-xs text-slate-400">ID: {company.id}</span>
                            </div>
                          </div>
                        </div>

                        {/* Action Icon */}
                        <div className="ml-4">
                          <ExternalLink className="h-5 w-5 text-slate-400" />
                        </div>
                      </div>

                      {/* Low Credit Warning */}
                      {showLowCreditWarning && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-sm text-warning-700">
                            ⚠️ Credits below threshold ({company.settings?.lowCreditThreshold || 100})
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">
                  {searchQuery ? 'No customers found' : 'No customers yet'}
                </h3>
                <p className="text-slate-500">
                  {searchQuery
                    ? 'Try adjusting your search query'
                    : 'Customers will appear here once they sign up'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Account Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create New Account"
          size="lg"
        >
          <div className="space-y-4">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Acme Corporation"
              />
            </div>

            {/* Domain */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Domain *
              </label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="acme.com"
              />
            </div>

            {/* Plan */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Plan
              </label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value as 'trial' | 'starter' | 'growth' | 'pro' | 'enterprise' })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            {/* Credits */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Initial Credits
              </label>
              <input
                type="number"
                value={formData.credits}
                onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="1000"
                min="0"
              />
            </div>

            {/* Internal Account Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isInternal"
                checked={formData.isInternal}
                onChange={(e) => setFormData({ ...formData, isInternal: e.target.checked })}
                className="h-4 w-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="isInternal" className="text-sm font-medium text-slate-700">
                Mark as internal account (unlimited credits)
              </label>
            </div>

            {/* Address */}
            <div className="border-t border-slate-200 pt-4 space-y-3">
              <h4 className="text-sm font-semibold text-slate-900">Address (Optional)</h4>

              <div>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData({ ...formData, address: { ...formData.address, street: e.target.value } })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Street Address"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => setFormData({ ...formData, address: { ...formData.address, city: e.target.value } })}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="City"
                />
                <input
                  type="text"
                  value={formData.address.state}
                  onChange={(e) => setFormData({ ...formData, address: { ...formData.address, state: e.target.value } })}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="State"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={formData.address.zipCode}
                  onChange={(e) => setFormData({ ...formData, address: { ...formData.address, zipCode: e.target.value } })}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="ZIP Code"
                />
                <input
                  type="text"
                  value={formData.address.country}
                  onChange={(e) => setFormData({ ...formData, address: { ...formData.address, country: e.target.value } })}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Country"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                isLoading={isCreating}
              >
                Create Account
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
